import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchLicenseAcceptanceReport } from '../api/auth';
import GoToDashboardButton from './GoToDashboardButton';
import styles from './AdminLicenseReportPage.module.css';

export default function AdminLicenseReportPage({ currentUser }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState({ search: '', packageKey: '' });
  const [acceptances, setAcceptances] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchLicenseAcceptanceReport(filters);
      setAcceptances(Array.isArray(data.acceptances) ? data.acceptances : []);
      setPackages(Array.isArray(data.packages) ? data.packages : []);
    } catch (loadError) {
      setError(loadError.message || t('Unable to load license acceptance report.'));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadReport();
  }, [currentUser?.role, loadReport, navigate]);

  if (currentUser?.role !== 'admin') return null;

  return (
    <main className={styles.container}>
      <GoToDashboardButton />
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{t('Administration')}</p>
          <h1 className={styles.title}>{t('License Acceptance Report')}</h1>
          <p className={styles.subtitle}>
            {t('Review who accepted which Smarti license package, when they accepted it, and from which source or IP address.')}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.secondaryButton} onClick={() => navigate('/admin')}>
            {t('Back to Admin Panel')}
          </button>
          <button type="button" className={styles.primaryButton} onClick={loadReport}>
            {t('Refresh')}
          </button>
        </div>
      </header>

      <section className={styles.filters}>
        <input
          value={filters.search}
          onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
          placeholder={t('Search username, IP, version, or package')}
        />
        <select
          value={filters.packageKey}
          onChange={(event) => setFilters((current) => ({ ...current, packageKey: event.target.value }))}
        >
          <option value="">{t('All license packages')}</option>
          {packages.map((item) => (
            <option key={item.key} value={item.key}>{item.label}</option>
          ))}
        </select>
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}

      <section className={styles.panel}>
        {loading ? (
          <div className={styles.emptyState}>{t('Loading license acceptances...')}</div>
        ) : acceptances.length === 0 ? (
          <div className={styles.emptyState}>{t('No license acceptances found for the selected filters.')}</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('User')}</th>
                  <th>{t('Package')}</th>
                  <th>{t('License version')}</th>
                  <th>{t('Accepted')}</th>
                  <th>{t('Source')}</th>
                  <th>{t('Accepted IP')}</th>
                </tr>
              </thead>
              <tbody>
                {acceptances.map((row) => (
                  <tr key={row.id}>
                    <td>{row.username}</td>
                    <td>{row.licensePackage || '-'}</td>
                    <td>{row.licenseVersion || '-'}</td>
                    <td>{formatDate(row.acceptedAt, i18n.language)}</td>
                    <td>{row.acceptanceSource || '-'}</td>
                    <td>{row.acceptedIp || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}

function formatDate(value, locale) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale || undefined);
}
